/**
 * Embed-aware HTML rendering helpers for the Blox page designer.
 *
 * Inside the designer preview (`/_blox_preview`) an `<iframe>` whose provider
 * refuses framing (`frame-ancestors` / `X-Frame-Options` — Neon donation forms,
 * most SaaS widgets, …) paints the browser's broken-frame error instead of the
 * embed. Since a blocked frame cannot be detected from script, we render a quiet
 * "no preview here" placeholder for any embed we cannot assume is frameable.
 *
 * Providers that DO allow framing (video, maps, forms, …) keep rendering so the
 * designer still previews them normally.
 */

/** Hosts known to serve permissive framing headers — left untouched in preview. */
export const FRAMEABLE_HOSTS = [
	'youtube.com',
	'youtube-nocookie.com',
	'youtu.be',
	'vimeo.com',
	'dailymotion.com',
	'twitch.tv',
	'facebook.com',
	'instagram.com',
	'spotify.com',
	'soundcloud.com',
	'google.com',
	'googleapis.com',
	'gstatic.com',
	'recaptcha.net',
	'hcaptcha.com',
	'calendly.com',
	'cal.com',
	'typeform.com',
	'airtable.com',
	'notion.site',
	'miro.com',
	'figma.com',
	'codepen.io',
	'jsfiddle.net',
]

/** `<iframe …>…</iframe>` or a self-closing `<iframe … />`, case-insensitive. */
const IFRAME_RE = /<iframe\b([^>]*)>[\s\S]*?<\/iframe>|<iframe\b([^>]*)\/?>/gi

const DEFAULT_STUB_HEIGHT = 320
const MIN_STUB_HEIGHT = 180
const MAX_STUB_HEIGHT = 900

export function embedAttr(attrs: string, name: string): string {
	const match = attrs.match(new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i'))
	return match?.[2] ?? match?.[3] ?? match?.[4] ?? ''
}

export function embedHost(src: string): string {
	if (!src) return ''
	try {
		return new URL(src, 'https://embed.invalid').hostname.replace(/^www\./, '').toLowerCase()
	} catch {
		return ''
	}
}

export function isFrameable(src: string): boolean {
	const host = embedHost(src)
	if (!host) return false
	return FRAMEABLE_HOSTS.some(allowed => host === allowed || host.endsWith(`.${allowed}`))
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function clampHeight(height: string | number | undefined): number {
	const parsed = Number.parseInt(String(height ?? ''), 10)
	if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_STUB_HEIGHT
	return Math.min(Math.max(parsed, MIN_STUB_HEIGHT), MAX_STUB_HEIGHT)
}

export interface EmbedInfo {
	src?: string
	height?: string | number
}

/** Placeholder markup shown in place of an embed that cannot be framed. */
export function embedPlaceholder(info: EmbedInfo): string {
	const src = info.src ?? ''
	const origin = embedHost(src)

	return [
		`<div class="blox-embed-stub grid justify-items-center align-content-center gap-05 bg-gray-20 border radius-1 px-1 py-2 txt-center" data-blox-embed-stub style="min-height:${clampHeight(info.height)}px">`,
		`<span class="txt11 uppercase tracking-wide opacity-6">Embedded content</span>`,
		`<span class="txt15 semi">Preview not available in Blox</span>`,
		`<span class="txt12 opacity-6 ellipsis-1 w-100p monospace">${escapeHtml(origin || src || 'iframe')}</span>`,
		`</div>`,
	].join('')
}

/**
 * Replaces embeds that cannot be assumed frameable with a placeholder.
 * Frameable providers (YouTube, Vimeo, Maps, …) are left untouched.
 */
export function stubUnframeableEmbeds(html: string): string {
	if (!html) return ''
	return html.replace(IFRAME_RE, (tag: string, paired: string, single: string) => {
		const attrs = paired ?? single ?? ''
		const src = embedAttr(attrs, 'src')
		return isFrameable(src) ? tag : embedPlaceholder({ src, height: embedAttr(attrs, 'height') })
	})
}

/**
 * True when the current document is the Blox designer preview — either framed by
 * the editor, or loaded directly at the `/_blox_preview` route.
 */
export function isBloxDesignerPreview(): boolean {
	if (typeof window === 'undefined') return false
	try {
		if (window.top !== window.self) return true
	} catch {
		// Cross-origin frame access throws → we're framed → this is the preview.
		return true
	}
	return window.location?.pathname?.startsWith('/_blox_preview') ?? false
}

function frameSource(frame: HTMLIFrameElement): string {
	return frame.getAttribute('src') ?? frame.getAttribute('data-src') ?? ''
}

function replaceFrame(frame: HTMLIFrameElement): void {
	if (isFrameable(frameSource(frame))) return
	const host = frame.ownerDocument.createElement('div')
	host.innerHTML = embedPlaceholder({
		src: frameSource(frame),
		height: frame.getAttribute('height') ?? '',
	})
	const stub = host.firstElementChild
	if (!stub) return
	frame.replaceWith(stub)
}

function scan(root: ParentNode, FrameCtor: typeof HTMLIFrameElement | undefined): void {
	root.querySelectorAll('iframe').forEach(frame => {
		if (FrameCtor && frame instanceof FrameCtor) replaceFrame(frame)
	})
}

/**
 * Designer-only safety net: any `<iframe>` that reaches the preview DOM — inside
 * a rich-text field, a popup, an article, or any other block — is swapped for the
 * placeholder when its provider can't be framed. Complements `<RichHtml>`, which
 * handles the HTML we render ourselves.
 *
 * Idempotent, and a no-op outside the designer preview.
 */
export function installEmbedPreviewGuard(target?: Document): () => void {
	const doc = target ?? (typeof document === 'undefined' ? undefined : document)
	const view = doc?.defaultView
	if (!doc || !view || typeof view.MutationObserver === 'undefined') return () => {}

	const FrameCtor = view.HTMLIFrameElement
	const Observer = view.MutationObserver

	scan(doc, FrameCtor)

	const observer = new Observer(records => {
		for (const record of records) {
			if (record.type === 'attributes') {
				const el = record.target
				if (FrameCtor && el instanceof FrameCtor && el.isConnected) replaceFrame(el)
				continue
			}
			for (const node of record.addedNodes) {
				if (node.nodeType !== 1) continue
				const el = node as Element
				if (FrameCtor && el instanceof FrameCtor) replaceFrame(el)
				else scan(el, FrameCtor)
			}
		}
	})

	observer.observe(doc.documentElement ?? doc, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['src'],
	})

	return () => observer.disconnect()
}
